<script>
	import { CONFIG } from '$lib/config/index.js';
	import { detectBrokenImage } from '$lib/utils/images.js';
	import { bannerFitClass } from '$lib/utils/banner.js';

	/** @type {import('./$types').PageData} */
	export let data;

	const SITE_URL = CONFIG.site.url;
	// Shared-link card. Listing pages use the site-wide card; per-article and
	// per-issue cards are generated at ingest.
	$: ogImage = `${SITE_URL}/og-default.png`;
	$: ogImageAlt = `${CONFIG.site.name} — ${CONFIG.conference.name}`;

	const DATA_URL = CONFIG.data.url;

	/** @type {any[]} */
	$: issues = data.issues || [];

	/** @type {{totalArticles: number, totalIssues: number, yearRange?: {start: number, end: number}}|null} */
	$: stats = data.manifest?.stats || null;

	$: yearRange = stats?.yearRange || null;

	$: currentIssue =
		issues.find((issue) => issue.id === CONFIG.conference.currentIssueId) || issues[0] || null;
	$: conference = currentIssue?.conference || null;
	let bannerFailed = false;
	$: bannerFit = bannerFitClass(conference?.banner);
	$: bannerUrl =
		conference?.banner && !bannerFailed
			? `${DATA_URL}/issues/${currentIssue.id}/${conference.banner}`
			: null;

	/** @type {string[]} */
	$: currentIssueMeta = [
		conference?.location,
		conference?.dates,
		currentIssue?.articleCount ? `${currentIssue.articleCount} articles` : null
	].filter(Boolean);

	// Oldest first for the archive strip
	$: archiveIssues = [...issues].sort((a, b) => (a.year || 0) - (b.year || 0));

	$: metaDescription = stats
		? `DCPapers is a diamond open access repository for Dublin Core Metadata Initiative conference proceedings, featuring ${stats.totalArticles} peer-reviewed articles from ${yearRange ? `${yearRange.start} to ${yearRange.end}` : '2001 to present'}.`
		: 'DCPapers is a diamond open access repository for Dublin Core Metadata Initiative conference proceedings, featuring over 600 peer-reviewed articles from 2001 to present.';

	const indexingLogos = [
		{ src: '/images/logos/landing/doi_logo_v001.svg', alt: 'DOI' },
		{ src: '/images/logos/landing/orcid_logo_v001.svg', alt: 'ORCID' },
		{ src: '/images/logos/landing/wikidata-logo-en_v001.svg', alt: 'Wikidata' },
		{ src: '/images/logos/landing/dblp_logo_v001.png', alt: 'DBLP' },
		{ src: '/images/logos/landing/acm-dl_logo_v001.svg', alt: 'ACM Digital Library' },
		{ src: '/images/logos/landing/semantic_scholar_logo_v001.svg', alt: 'Semantic Scholar' },
		{ src: '/images/logos/landing/openalex_logo_v001.png', alt: 'OpenAlex' },
		{ src: '/images/logos/landing/scholar_logo_v001.png', alt: 'Google Scholar' },
		{ src: '/images/logos/landing/scopus_logo_v001.svg', alt: 'Scopus' },
		{ src: '/images/logos/landing/worldcat_logo_v001.svg', alt: 'WorldCat' }
	];
</script>

<svelte:head>
	<title>DCPapers: Dublin Core Papers and Proceedings</title>
	<meta name="description" content={metaDescription} />
	<link rel="canonical" href={SITE_URL} />

	<!-- Open Graph -->
	<meta property="og:type" content="website" />
	<meta property="og:url" content={SITE_URL} />
	<meta property="og:title" content="DCPapers: Dublin Core Papers and Proceedings" />
	<meta
		property="og:description"
		content="Diamond open access repository for Dublin Core Metadata Initiative conference proceedings"
	/>
	<meta property="og:site_name" content="DCPapers" />

	<!-- Twitter -->
	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:title" content="DCPapers: Dublin Core Papers and Proceedings" />
	<meta
		name="twitter:description"
		content="Diamond open access repository for Dublin Core Metadata Initiative conference proceedings"
	/>
	<meta property="og:image" content={ogImage} />
	<meta property="og:image:width" content="1200" />
	<meta property="og:image:height" content="630" />
	<meta property="og:image:alt" content={ogImageAlt} />
	<meta name="twitter:image" content={ogImage} />
</svelte:head>

<div class="mx-auto max-w-7xl px-6 lg:px-8">
	<!-- Masthead hero -->
	<section class="pt-16 pb-12 sm:pt-20 sm:pb-14" aria-labelledby="masthead-heading">
		<p class="text-[13px] font-semibold tracking-[0.08em] text-accent-700 uppercase">
			<!-- Same target and the same mild underline as the footer's mark, so the
			     claim is verifiable wherever a reader meets it. The line is already
			     accent-coloured, so an underline is what distinguishes the link from
			     the label around it. -->
			<a
				href={CONFIG.license.openAccessUrl}
				target="_blank"
				rel="noopener"
				class="underline decoration-accent-700/30 underline-offset-2 hover:decoration-accent-700"
				>Diamond open access</a
			>
			&middot; ISSN <span class="tabular-nums">{CONFIG.conference.issn}</span>
		</p>
		<h1
			id="masthead-heading"
			class="mt-4 max-w-3xl text-4xl font-semibold tracking-tight text-balance text-stone-900 sm:text-5xl"
		>
			Twenty-five years of metadata research, open to everyone
		</h1>
		<p class="mt-5 max-w-2xl text-lg/8 text-stone-600">
			Peer-reviewed proceedings of the International Conference on Dublin Core and Metadata
			Applications, published by DCMI since 2001.
		</p>

		<form action="/search" method="GET" class="mt-8 flex max-w-2xl items-stretch gap-x-3">
			<label for="home-search" class="sr-only">Search articles</label>
			<div class="relative min-w-0 flex-auto">
				<div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
					<svg
						class="size-5 text-stone-400"
						viewBox="0 0 20 20"
						fill="currentColor"
						aria-hidden="true"
					>
						<path
							fill-rule="evenodd"
							d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
							clip-rule="evenodd"
						/>
					</svg>
				</div>
				<input
					id="home-search"
					name="q"
					type="search"
					required
					class="block w-full rounded-full border border-stone-200 bg-stone-50 py-3 pr-4 pl-11 text-base text-stone-900 placeholder:text-stone-400 focus:border-accent-600 focus:bg-white focus:ring-2 focus:ring-accent-600/20 focus:outline-none"
					placeholder="Search titles, authors, and abstracts"
				/>
			</div>
			<button
				type="submit"
				class="shrink-0 rounded-md bg-accent-700 px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-accent-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-700"
			>
				Search
			</button>
		</form>

		{#if stats}
			<p class="mt-6 text-sm text-stone-500 tabular-nums">
				{stats.totalArticles} articles &middot; {stats.totalIssues} issues{yearRange
					? ` · ${yearRange.start}–${yearRange.end}`
					: ''}
			</p>
		{/if}
	</section>

	<!-- Current issue -->
	{#if currentIssue}
		<section class="pb-14 sm:pb-16" aria-labelledby="current-issue-heading">
			<div
				class="overflow-hidden rounded-lg border border-stone-200 {bannerUrl
					? 'md:grid md:grid-cols-2'
					: ''}"
			>
				{#if bannerUrl}
					<img
						src={bannerUrl}
						alt="{currentIssue.shortTitle || currentIssue.title} conference banner"
						class="h-56 w-full border-b border-stone-200 sm:h-64 md:h-full md:max-h-96 md:border-r md:border-b-0 {bannerFit}"
						use:detectBrokenImage={() => (bannerFailed = true)}
					/>
				{/if}
				<div class="p-6 sm:p-8 lg:p-10">
					<p class="text-[13px] font-semibold tracking-[0.08em] text-accent-700 uppercase">
						Current issue
					</p>
					<h2
						id="current-issue-heading"
						class="mt-3 text-2xl font-semibold tracking-tight text-balance text-stone-900"
					>
						{currentIssue.title}
					</h2>
					{#if conference?.theme}
						<p class="mt-3 font-serif text-lg/7 text-stone-600 italic">{conference.theme}</p>
					{/if}
					{#if currentIssueMeta.length}
						<p class="mt-4 text-sm text-stone-500 tabular-nums">
							{currentIssueMeta.join(' · ')}
						</p>
					{/if}
					<a
						href="/issue/{currentIssue.id}"
						class="mt-6 inline-flex items-center gap-x-2 rounded-md bg-accent-700 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-accent-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-700"
					>
						Browse {currentIssue.shortTitle || currentIssue.title}
						<svg class="size-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
							<path
								fill-rule="evenodd"
								d="M3 10a.75.75 0 0 1 .75-.75h10.638L10.23 5.29a.75.75 0 1 1 1.04-1.08l5.5 5.25a.75.75 0 0 1 0 1.08l-5.5 5.25a.75.75 0 1 1-1.04-1.08l4.158-3.96H3.75A.75.75 0 0 1 3 10Z"
								clip-rule="evenodd"
							/>
						</svg>
					</a>
				</div>
			</div>
		</section>
	{/if}

	<!-- Browse the archive -->
	{#if archiveIssues.length}
		<section class="border-t border-stone-200 py-10" aria-labelledby="archive-heading">
			<h2
				id="archive-heading"
				class="text-[11px] font-semibold tracking-[0.1em] text-stone-400 uppercase"
			>
				Browse the archive
			</h2>
			<div class="mt-4 flex flex-wrap items-baseline gap-2">
				{#each archiveIssues as issue (issue.id)}
					<a
						href="/issue/{issue.id}"
						class="rounded-full bg-stone-100 px-3 py-1 text-[13px] text-stone-700 tabular-nums transition-colors hover:bg-accent-50 hover:text-accent-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-700"
					>
						{issue.year}
					</a>
				{/each}
				<a
					href="/issues"
					class="ml-1 text-[13px] font-medium text-accent-700 transition-colors hover:text-accent-800"
				>
					All issues <span aria-hidden="true">&rarr;</span>
				</a>
			</div>
		</section>
	{/if}

	<!-- Indexing -->
	<section class="border-t border-stone-200 py-10 sm:pb-14" aria-labelledby="indexing-heading">
		<h2
			id="indexing-heading"
			class="text-[11px] font-semibold tracking-[0.1em] text-stone-400 uppercase"
		>
			Indexed and referenced in
		</h2>
		<div class="mt-8 grid grid-cols-2 items-center gap-x-10 gap-y-8 sm:grid-cols-3 lg:grid-cols-5">
			{#each indexingLogos as logo (logo.src)}
				<img
					src={logo.src}
					alt={logo.alt}
					width="158"
					height="48"
					loading="lazy"
					class="mx-auto max-h-10 w-full object-contain opacity-70 grayscale transition hover:opacity-100 hover:grayscale-0"
				/>
			{/each}
		</div>
	</section>
	<!-- Open access -->
	<!-- No fill and no full-bleed rule: this is the last row of the same stack as
	     the archive and indexing sections, so it takes their exact classes and its
	     rule is drawn inside the container padding, aligning with the headings
	     above rather than running the width of the viewport. -->
	<section class="border-t border-stone-200 py-10" aria-labelledby="open-access-heading">
		<h2 id="open-access-heading" class="sr-only">Open access and licensing</h2>
		<div class="grid gap-x-12 gap-y-8 md:grid-cols-2">
			<div class="flex items-start gap-x-4">
				<img
					src="/images/logos/licenses/cc-by.svg"
					alt="CC BY 4.0"
					width="120"
					height="42"
					loading="lazy"
					class="h-8 w-auto shrink-0"
				/>
				<p class="text-sm/6 text-stone-600">
					Papers are published under the
					<a
						href={CONFIG.license.url}
						rel="license"
						class="font-medium text-stone-700 underline decoration-stone-300 underline-offset-2 hover:text-accent-700"
						>Creative Commons Attribution 4.0</a
					> license. Read, share, and adapt them with credit to the authors.
				</p>
			</div>
			<div class="flex items-start gap-x-4">
				<img
					src="/images/logos/licenses/cc-zero.svg"
					alt="CC0 1.0"
					width="88"
					height="31"
					loading="lazy"
					class="h-8 w-auto shrink-0"
				/>
				<p class="text-sm/6 text-stone-600">
					Metadata is dedicated to the public domain under
					<a
						href={CONFIG.license.metadataLicense}
						class="font-medium text-stone-700 underline decoration-stone-300 underline-offset-2 hover:text-accent-700"
						>CC0 1.0</a
					>. Reuse it in catalogues, indexes, and research without asking permission.
				</p>
			</div>
		</div>
	</section>
</div>
